"use client"

import { useEffect } from "react"
import posthog from 'posthog-js'
import { isHandleableWebpackError } from "@/utils/webpack-error-detector"

interface ClientErrorDetectorProps {
  onWebpackError?: () => void
}

export function ClientErrorDetector({ onWebpackError }: ClientErrorDetectorProps) {
  useEffect(() => {

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason
      
      if (isHandleableWebpackError(error)) {
        console.warn("Detected webpack module loading error in promise rejection:", error)
        
        posthog.captureException(error, {
          $set: { 
            error_type: "webpack_module_loading_unhandled",
            error_context: "promise_rejection",
            user_agent: navigator.userAgent,
            page_url: window.location.href
          }
        })
        
        onWebpackError?.()
        event.preventDefault()
      }
    }

    const handleError = (event: ErrorEvent) => {
      const error = event.error
      
      if (isHandleableWebpackError(error)) {
        console.warn("Detected webpack module loading error:", error)
        
        posthog.captureException(error, {
          $set: { 
            error_type: "webpack_module_loading_window",
            error_context: "window_error",
            user_agent: navigator.userAgent,
            page_url: window.location.href,
            file_name: event.filename,
            line_number: event.lineno,
            column_number: event.colno
          }
        })
        
        onWebpackError?.()
        event.preventDefault()
      }
    }

    const handleSecurityPolicyViolation = (event: SecurityPolicyViolationEvent) => {
      if (event.violatedDirective?.includes('script-src') && 
          event.blockedURI?.includes('webpack')) {
        console.warn("CSP violation related to webpack detected:", event)
        
        posthog.capture('webpack_csp_violation', {
          violated_directive: event.violatedDirective,
          blocked_uri: event.blockedURI,
          source_file: event.sourceFile
        })
      }
    }

    const detectStaleWebpackChunks = () => {
      const scripts = document.querySelectorAll('script[src*="chunk"]')
      const links = document.querySelectorAll('link[href*="chunk"]')
      
      const allAssets = [...Array.from(scripts), ...Array.from(links)]
      
      allAssets.forEach(async (asset) => {
        const url = (asset as HTMLScriptElement).src || (asset as HTMLLinkElement).href
        if (!url) return
        
        try {
          const response = await fetch(url, { method: 'HEAD', cache: 'no-cache' })
          if (!response.ok && response.status === 404) {
            console.warn("Stale webpack chunk detected:", url)
            posthog.capture('stale_webpack_chunk_detected', { 
              chunk_url: url,
              response_status: response.status 
            })
          }
        } catch (error) {
          console.warn("Error checking webpack chunk:", url, error)
        }
      })
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    window.addEventListener('error', handleError)
    window.addEventListener('securitypolicyviolation', handleSecurityPolicyViolation)
    
    const chunkCheckInterval = setInterval(detectStaleWebpackChunks, 30000)
    
    const visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(detectStaleWebpackChunks, 1000)
      }
    }
    document.addEventListener('visibilitychange', visibilityChangeHandler)

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('error', handleError)
      window.removeEventListener('securitypolicyviolation', handleSecurityPolicyViolation)
      document.removeEventListener('visibilitychange', visibilityChangeHandler)
      clearInterval(chunkCheckInterval)
    }
  }, [onWebpackError])

  return null
}